import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { fetchTransactionDetailsAsync } from "../../../redux/features/analyticsSlice";
import moment from "moment";
import { formatNumberWithCommas } from "../../../helpers";
import { useNavigate, useParams } from "react-router-dom";

const TransactionDetails = () => {
  const { transactionDetails, loading } = useAppSelector(
    (state) => state.analytics
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: transactionId } = useParams<string>();

  useEffect(() => {
    if (transactionId) {
      dispatch(fetchTransactionDetailsAsync(transactionId));
    }
  }, [dispatch, transactionId]);

  const data = transactionDetails?.data;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-gray-500">Loading transaction details...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-gray-500">No transaction found.</p>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-4 py-8">
      <button
        className="text-gray-500 hover:text-gray-800 flex items-center font-bold mb-4 bg-gray-50 p-2 rounded"
        onClick={() => navigate(-1)}
      >
        &larr; Back
      </button>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Transaction Info */}
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Transaction Info
          </h3>
          <p>
            <span className="font-medium text-gray-600">Type:</span>{" "}
            {data?.type}
          </p>
          <p>
            <span className="font-medium text-gray-600">Status:</span>{" "}
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-semibold
      ${
        data.status === "COMPLETED"
          ? "bg-green-100 text-green-700"
          : data.status === "PENDING"
          ? "bg-yellow-100 text-yellow-700"
          : data.status === "FAILED"
          ? "bg-red-100 text-red-700"
          : data.status === "CANCELLED"
          ? "bg-rose-100 text-rose-700"
          : data.status === "EXPIRED"
          ? "bg-slate-100 text-slate-700"
          : "bg-gray-100 text-gray-700"
      }`}
            >
              {data.status}
            </span>
          </p>

          <p>
            <span className="font-medium text-gray-600">Description:</span>{" "}
            {data?.description}
          </p>
          <p>
            <span className="font-medium text-gray-600">Date:</span>{" "}
            {moment(data?.createdAt).format("Do MMMM YYYY, h:mm A")}
          </p>
        </div>

        {/* Amount & Provider */}
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Payment Details
          </h3>
          <p>
            <span className="font-medium text-gray-600">Amount:</span> ₦
            {formatNumberWithCommas(Number(data?.amount))}
          </p>
          <p>
            <span className="font-medium text-gray-600">Fee:</span> ₦
            {formatNumberWithCommas(Number(data?.fee))}
          </p>
          <p>
            <span className="font-medium text-gray-600">Payment Provider:</span>{" "}
            {data.paymentProvider}
          </p>
          <p>
            <span className="font-medium text-gray-600">Reference ID:</span>{" "}
            {data.refId}
          </p>
        </div>

        {/* User Info */}
        <div className="bg-white p-6 rounded-xl shadow-sm border md:col-span-2">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            User Information
          </h3>
          <p>
            <span className="font-medium text-gray-600">Full Name:</span>{" "}
            {data?.user?.firstName} {data?.user?.lastName}
          </p>
          <p>
            <span className="font-medium text-gray-600">Email:</span>{" "}
            {data?.user?.email}
          </p>
          <p>
            <span className="font-medium text-gray-600">User ID:</span>{" "}
            {data?.user?.id}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetails;
